import { ManOutlined, WomanOutlined } from '@ant-design/icons';
import { ProCard } from '@ant-design/pro-components';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Button, Descriptions, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

/**
 * APIs
 */
import { rUser } from '@/apis/user';

/**
 * Contexts
 */
import { useLayoutStore } from '@/store/layout';

/**
 * Types
 */
import type { UserWithRoleUsersDto } from '@/dtos/user.dto';
import type { DescriptionsProps } from 'antd';

const PageUserDetail: React.FC = () => {
    /**
     * Params
     */
    const params = useParams();

    /**
     * Hooks
     */
    const { setPageContainer } = useLayoutStore();

    const { t } = useTranslation();

    /**
     * Requests
     */
    const { data: dataRUser } = useQuery({
        queryKey: ['rUser', params.id],
        queryFn: () => rUser(params.id!),
    });

    /**
     * ChildrenProps
     */
    const descriptionsProps: DescriptionsProps = {
        column: 2,
        items: [
            {
                children: <Typography.Text copyable>{dataRUser?.data?.data?.id}</Typography.Text>,
                label: 'ID',
            },
            {
                children: dataRUser?.data?.data?.bio ?? '-',
                label: t('bio'),
            },
            {
                children: (
                    <>
                        {(dataRUser?.data?.data as UserWithRoleUsersDto)?.roleUsers?.map((ru) => (
                            <Tag bordered={false} key={ru.role_id}>
                                {ru.role.name}
                            </Tag>
                        )) ?? '-'}
                    </>
                ),
                label: t('roles'),
            },
            {
                children: dayjs(dataRUser?.data?.data?.created_at).format('YYYY-MM-DD HH:mm:ss'),
                label: t('created_at'),
            },
            {
                children: dataRUser?.data?.data?.email ?? '-',
                label: t('email'),
            },
            {
                children: dayjs(dataRUser?.data?.data?.updated_at).format('YYYY-MM-DD HH:mm:ss'),
                label: t('updated_at'),
            },
            {
                children: dataRUser?.data?.data?.mobile ?? '-',
                label: t('mobile'),
            },
        ],
        layout: 'vertical',
        size: 'middle',
        title: (
            <Space>
                <Avatar
                    alt=""
                    size="large"
                    src={dataRUser?.data?.data?.avatar && `${import.meta.env.VITE_CDN}/${dataRUser.data.data.avatar}`}
                >
                    {dataRUser?.data?.data?.nickname?.charAt(0).toUpperCase()}
                </Avatar>

                <span>
                    {dataRUser?.data?.data?.nickname} (@{dataRUser?.data?.data?.username})
                </span>

                {dataRUser?.data?.data?.gender === 'male' ? <ManOutlined style={{ color: '#3B82F6' }} /> : null}

                {dataRUser?.data?.data?.gender === 'female' ? <WomanOutlined style={{ color: '#EC4899' }} /> : null}

                <span>
                    {dataRUser?.data?.data?.is_deleted ? (
                        <Tag bordered={false} color="red">
                            {t('status_deleted')}
                        </Tag>
                    ) : dataRUser?.data?.data?.is_disabled ? (
                        <Tag bordered={false} color="red">
                            {t('status_disabled')}
                        </Tag>
                    ) : (
                        <Tag bordered={false} color="green">
                            {t('status_enabled')}
                        </Tag>
                    )}
                </span>
            </Space>
        ),
    };

    /**
     * Effects
     */
    useEffect(() => {
        setPageContainer({
            extra: (
                <Link to="/system-management/users">
                    <Button type="primary">{t('back')}</Button>
                </Link>
            ),
        });

        return () => {
            setPageContainer({});
        };
    }, []);

    return (
        <ProCard>
            <Descriptions {...descriptionsProps} />
        </ProCard>
    );
};

export default PageUserDetail;
